/**
 * SitePick front — 実物確認画面（`/sites/{slug}/live/`）の「自社の情報で見る」。docs/22。
 *
 * 購入前の訪問者が、デモの TOP を自社のロゴ・会社名・写真・色・文章に置き換えて確かめる（A1）。
 * 親（この画面）からデモ（sandbox の iframe・不透明オリジン）の DOM には触れない。やり取りは postMessage だけ:
 *   親 → デモ: apply / asset / mode / focus / reveal（`{ type: "sitepick:demo-customize:<name>", v: 1, ... }`）
 *   デモ → 親: ready / manifest / applied / select / blocked / scroll / coach
 * 形は試作（PoC）と同じ。デモ側の受け口は各デモ HTML の SITE PICK DEMO CUSTOMIZE ブロック。
 *
 * 守ること（docs/22 §7）:
 *   - 受け取るのは `e.source === iframe.contentWindow` かつ origin が "null"（sandbox）か自オリジンのものだけ。
 *   - 枠の信頼フラグ: 親が起こしていない読み込み（デモの中のリンクで移った等）の後は、何が届いても欄を有効にせず、
 *     apply / asset / mode / focus / reveal を一切送らない。立て直しは「デモのトップに戻す」（親が src を設定し直す）だけ。
 *   - 送るのは ref・文字の行・asset id・Blob・数値だけ。デモから届いた文字列は textContent でだけ描く（innerHTML は使わない）。
 *   - 置き換えた内容は親のメモリの中だけ（customize-state.js）。サーバへ送らない。ブラウザの保存領域には
 *     案内カードを表示したかどうかの印（"1" だけ）を localStorage に置く。
 *   - 計測は analytics.js の customizeDemo / customizeError だけ（列挙値のみ。入力した値は送らない）。
 *   - ページの構成の操作（並べ替え・非表示・複製）と「ひとつ前に戻す」は A2。ここでは出さない。
 * 画面の文言はすべて PHP（inc/live-customize.php の sitepick_customize_copy()）から data 属性で受け取る。
 */

import { customizeDemo, customizeError } from "./analytics.js";
import { announce } from "./live-region.js";
import { copyText } from "./clipboard.js";
import { createState } from "./customize-state.js";
import { prepareImage, ratioOf } from "./customize-image.js";
import { buildMemo, fmt, headOf } from "./customize-memo.js";

const NS = "sitepick:demo-customize:";
const REF_RE = /^[sti]:\d{1,3}(?::\d{1,3})?(?:~[cs]\d{1,2})?$/;
const SIZE_KEYS = ["s", "m", "l"];
const TABS = ["logo", "photo", "text", "color"];
const LANDING_KEY = "sitepick_cz_landing_seen";
const ACCEPT = "image/jpeg,image/png,image/webp";

/**
 * 実スマホ相当のビューアか（CSS の SP ブレークポイントと同じ条件。preview.js と同じ）。
 *
 * @returns {boolean}
 */
function isPhoneViewer() {
  return Boolean(window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
}

/**
 * 要素を作る（文字は textContent だけ）。
 *
 * @param {string} tag
 * @param {string} [cls]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) {
    e.className = cls;
  }
  if (text !== undefined) {
    e.textContent = text;
  }
  return e;
}

/**
 * @param {string} cls
 * @param {string} text
 * @param {(e: Event) => void} onClick
 * @param {Object<string, string>} [attrs]
 * @returns {HTMLButtonElement}
 */
function button(cls, text, onClick, attrs = {}) {
  const b = /** @type {HTMLButtonElement} */ (el("button", cls, text));
  b.type = "button";
  for (const [k, v] of Object.entries(attrs)) {
    b.setAttribute(k, v);
  }
  b.addEventListener("click", onClick);
  return b;
}

/**
 * デモから届いた manifest を検査して、使う形だけを新しいオブジェクトへ写す（届いた値は信用しない）。
 *
 * @param {unknown} raw
 * @param {(src: string) => boolean} srcOk 一覧のサムネに使ってよい URL か
 * @returns {Object}
 */
export function sanitizeManifest(raw, srcOk = () => false) {
  const out = { company: "", sections: [], texts: [], images: [], sizes: { s: 0.85, m: 1, l: 1.15 }, logo: { ready: false } };
  if (!raw || typeof raw !== "object") {
    return out;
  }
  const N = (v, lo, hi) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : 0;
  };
  const S = (v, n = 40) => (typeof v === "string" ? v.slice(0, n) : "");
  const R = (v) => (REF_RE.test(String(v)) ? String(v) : "");
  if (raw.sizes && typeof raw.sizes === "object") {
    for (const k of SIZE_KEYS) {
      const f = Number(raw.sizes[k]);
      if (Number.isFinite(f) && f >= 0.5 && f <= 2) {
        out.sizes[k] = f;
      }
    }
  }
  if (raw.logo && typeof raw.logo === "object" && raw.logo.ready === true) {
    const min = N(raw.logo.min, 16, 2000);
    const max = N(raw.logo.max, 16, 2000);
    out.logo = {
      ready: min > 0 && max >= min,
      min,
      max,
      max_height: N(raw.logo.max_height, 8, 2000),
      width: N(raw.logo.width, 0, 4000),
    };
  }
  out.company = S(raw.company, 60);
  for (const s of Array.isArray(raw.sections) ? raw.sections.slice(0, 40) : []) {
    if (!s || !R(s.ref)) {
      continue;
    }
    out.sections.push({ ref: String(s.ref), key: S(s.key, 30), label: S(s.label, 30), fixed: S(s.fixed, 8) });
  }
  for (const t of Array.isArray(raw.texts) ? raw.texts.slice(0, 400) : []) {
    if (!t || !R(t.ref)) {
      continue;
    }
    const rows = Array.isArray(t.rows) ? t.rows.slice(0, 8).map((r) => S(r, 400)) : [];
    const orig = Array.isArray(t.orig) ? t.orig.slice(0, 8).map((r) => S(r, 400)) : rows;
    out.texts.push({ ref: String(t.ref), tag: S(t.tag, 12), rows, orig, fv: t.fv === true, section: R(t.section), item: R(t.item) });
  }
  for (const i of Array.isArray(raw.images) ? raw.images.slice(0, 80) : []) {
    if (!i || !R(i.ref)) {
      continue;
    }
    const shared = (Array.isArray(i.shared_with) ? i.shared_with.slice(0, 20) : []).map(String).filter((r) => REF_RE.test(r) && r !== String(i.ref));
    const src = typeof i.src === "string" && i.src.length <= 500 && srcOk(i.src) ? i.src : "";
    out.images.push({
      ref: String(i.ref),
      slides: Math.max(1, Math.min(20, Number(i.slides) || 1)),
      width: N(i.width, 0, 20000),
      height: N(i.height, 0, 20000),
      role: S(i.role, 8),
      section: R(i.section),
      alt: S(i.alt, 40),
      src,
      shared_with: shared,
    });
  }
  return out;
}

/**
 * 初期化。対象の要素が無い（無効・対象外の商品・別のページ）なら何もしない。
 *
 * @param {Document} [doc]
 * @returns {void}
 */
export function initLiveCustomize(doc = document) {
  const root = doc.querySelector("[data-sp-preview]");
  const layer = doc.querySelector(".js-sp-cz-layer");
  const panel = doc.querySelector(".js-sp-cz-panel");
  const toggle = doc.querySelector(".js-sp-cz-toggle");
  const iframe = root ? root.querySelector(".p-live__iframe") : null;
  if (!root || !layer || !panel || !toggle || !(iframe instanceof HTMLIFrameElement)) {
    return;
  }
  let config = null;
  try {
    config = JSON.parse(layer.getAttribute("data-sp-cz-config") || "null");
  } catch {
    config = null;
  }
  if (!config || typeof config !== "object" || !config.limits || !config.messages) {
    return;
  }

  // ---- 設定・文言 ----
  const L = config.limits;
  const num = (key, fallback) => (Number.isFinite(Number(L[key])) ? Number(L[key]) : fallback);
  const msg = (key) => (typeof config.messages[key] === "string" ? config.messages[key] : "");
  const itemId = String(config.itemId || "");
  const q = (sel) => doc.querySelector(sel);
  const qa = (sel) => Array.from(doc.querySelectorAll(sel));

  const rail = q(".js-sp-cz-rail");
  const entry = q(".js-sp-cz-entry");
  const resetTop = q(".js-sp-cz-reset-top");
  const toastEl = q(".js-sp-cz-toast");
  const stage = root.querySelector(".p-live__stage");
  const titleEl = root.querySelector(".p-live__title");
  const productTitle = titleEl ? titleEl.textContent.trim() : "";
  const pageUrl = window.location.href.replace(/[?#].*$/, "");

  const state = createState();
  /** @type {Object|null} */
  let manifest = null;
  /** @type {Object<string, string[]>} ref -> 元の行（最初に届いた manifest から） */
  const origTexts = Object.create(null);
  let ready = null;
  let everReady = false;
  let open = false;
  let collapsed = false;
  let selected = null; // { ref, kind }
  let textCursor = -1;
  let coachDone = false;
  let openedOnce = false;
  let lastLogoApplied = null;
  let lastTextSizes = Object.create(null);

  // 一覧のサムネに使ってよい画像 = 自オリジンで、デモのディレクトリの下の画像ファイルだけ（デモから届いた URL をそのまま使わない）。
  const demoDir = (() => {
    try {
      const u = new URL(iframe.getAttribute("data-sp-src") || "", window.location.href);
      return u.origin === window.location.origin ? u.pathname.replace(/[^/]*$/, "") : "";
    } catch {
      return "";
    }
  })();
  const srcOk = (v) => {
    if (!demoDir) {
      return false;
    }
    try {
      const u = new URL(v);
      return u.origin === window.location.origin && u.pathname.startsWith(demoDir) && /\.(?:webp|jpe?g|png|avif)$/i.test(u.pathname) && !u.search && !u.hash;
    } catch {
      return false;
    }
  };

  // ---- 計測 ----
  const device = () => (isPhoneViewer() ? "sp" : "pc");
  const sectionKey = (secRef) => {
    const s = manifest && secRef ? manifest.sections.find((x) => x.ref === secRef) : null;
    return s ? (s.fixed === "hero" ? "hero" : s.key.toLowerCase().replace(/^s-/, "")) : "";
  };
  const track = (action, section = "") => customizeDemo(itemId, action, { device: device(), section });
  const trackError = (reason) => customizeError(itemId, reason === "image_pixels" ? "image_too_large" : reason);

  // ---- トースト・読み上げ ----
  let toastTimer = 0;
  const hideToast = () => {
    if (toastEl) {
      toastEl.hidden = true;
      toastEl.textContent = "";
    }
  };
  const showToast = (text, action = null, ms = 0) => {
    if (!toastEl || !text) {
      return;
    }
    toastEl.textContent = "";
    toastEl.append(el("span", "p-live-customize-toast__text", text));
    if (action) {
      toastEl.append(
        button("p-live-customize-toast__action", action.label, () => {
          hideToast();
          action.run();
        })
      );
    }
    toastEl.hidden = false;
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(hideToast, ms || (action ? 9000 : num("toastMs", 4000)));
  };

  // ---- 画面の寸法（上部バーの下 / 下部バーの上。パネル・入口・トーストが使う） ----
  const visible = (node) => !!node && node.getClientRects().length > 0 && window.getComputedStyle(node).display !== "none";
  const bar = root.querySelector(".p-live__bar");
  const foot = root.querySelector(".p-live__foot");
  const mini = root.querySelector(".p-live__mini");
  const measure = () => {
    const vh = window.innerHeight;
    let top = 0;
    if (visible(bar) && window.getComputedStyle(bar).position !== "fixed") {
      top = Math.max(0, Math.round(bar.getBoundingClientRect().bottom));
    }
    // 画面の下側に見えているバー（SP のコンパクトバー / 下部バー。「その他」で重ねて開いたときは両方）の上端。
    let edge = vh;
    for (const n of [mini, foot]) {
      if (visible(n)) {
        const t = n.getBoundingClientRect().top;
        if (t > vh / 2 && t < edge) {
          edge = t;
        }
      }
    }
    const bottom = Math.max(0, Math.round(vh - edge));
    root.style.setProperty("--sp-cz-top", `${top}px`);
    root.style.setProperty("--sp-cz-bottom", `${bottom}px`);
  };

  // ---- 枠の状態機械・信頼フラグ（docs/22 §7.1.1） ----
  let trusted = true; // 親が起こしていない読み込みの後は false
  let navigating = true; // 親が起こした読み込みの ready を待っている間（前の文書へは送らない）
  let expectLoad = true; // 次の load は親が起こしたもの（最初の昇格は preview.js が行う）
  let readySeen = false; // load より先に ready が届いた
  let awaitingReady = false; // load の後、ready を待っている
  let readyCount = 0;
  let graceTimer = 0;
  let away = false;
  let untrusted = false;
  const canSend = () => trusted && !navigating && !away && !untrusted;
  const usable = () => canSend() && !!manifest;

  const post = (name, payload = {}) => {
    if (!iframe.contentWindow) {
      return;
    }
    // sandbox の iframe は不透明オリジンなので targetOrigin は "*"。送るのは ref・行・id・Blob・数値だけ。
    iframe.contentWindow.postMessage(Object.assign({ type: NS + name, v: 1 }, payload), "*");
  };

  // ---- 送信（apply は宣言的な全状態。seq で照合し、届かなければ 1 回だけ送り直す） ----
  let seq = 0;
  let ackTimer = 0;
  /** 反映の確認（applied）を待っている操作。最新の seq の確認が届いたら、それまでの分をまとめて処理する。 */
  let pending = [];
  const sendApply = (ui = "", trackAs = "", section = "") => {
    if (!canSend()) {
      return;
    }
    seq += 1;
    const mySeq = seq;
    if (ui || trackAs) {
      pending.push({ ui, track: trackAs, section });
      pending = pending.slice(-20);
    }
    post("apply", { seq: mySeq, state: state.toWire() });
    window.clearTimeout(ackTimer);
    ackTimer = window.setTimeout(() => {
      if (!canSend() || mySeq !== seq) {
        return;
      }
      post("apply", { seq: mySeq, state: state.toWire() });
      ackTimer = window.setTimeout(() => {
        if (mySeq !== seq) {
          return;
        }
        trackError("no_ack");
        showToast(msg("err_apply"), { label: msg("export_copy"), run: copyMemo });
      }, num("ackTimeoutMs", 3000));
    }, num("ackTimeoutMs", 3000));
  };
  const pushAssets = () => {
    if (!canSend()) {
      return;
    }
    for (const [id, a] of state.assetEntries()) {
      post("asset", { id, blob: a.blob, kind: state.data.logo && state.data.logo.asset === id ? "logo" : "image" });
    }
  };
  let manifestTimer = 0;
  const waitManifest = () => {
    if (manifest) {
      return;
    }
    window.clearTimeout(manifestTimer);
    manifestTimer = window.setTimeout(() => {
      if (!manifest && open && canSend()) {
        trackError("no_manifest");
        showToast(msg("err_prepare"));
      }
    }, num("manifestTimeoutMs", 6000));
  };
  const coverRight = () => {
    if (!open || isPhoneViewer()) {
      return 0;
    }
    const box = collapsed ? rail : panel;
    if (!box || box.hidden) {
      return 0;
    }
    const fr = iframe.getBoundingClientRect();
    const br = box.getBoundingClientRect();
    return Math.max(0, Math.min(Math.round(fr.width), Math.round(fr.right - br.left)));
  };
  const sendMode = () => {
    if (!canSend()) {
      return;
    }
    // たたんでいる間もプレビューの文章・写真は選べる（docs/22 §3.2）。閉じている間は選ばない（点線の枠も出さない）。
    // cover = PC でパネル（たたんだときはレール）がプレビューの右端を覆っている幅（px）。デモは初回の吹き出しをその外に置く。
    post("mode", { on: open, pick: open, touch: open && isPhoneViewer(), coach: open && !coachDone, cover: coverRight() });
  };
  const focusRef = (ref) => {
    if (canSend()) {
      post("focus", { ref, top: isPhoneViewer() });
    }
  };
  const reveal = (kind) => {
    if (canSend()) {
      post("reveal", { kind });
    }
  };

  // ---- 下層 / 信頼しない枠の案内 ----
  const awayBox = q(".js-sp-cz-away");
  const renderAway = () => {
    if (!awayBox) {
      return;
    }
    const show = everReady && (away || untrusted);
    awayBox.hidden = !show;
    const text = q(".js-sp-cz-away-text");
    if (text) {
      text.textContent = away ? msg("away") : msg("untrusted");
    }
    const reloadBtn = q(".js-sp-cz-reload-top");
    const restoreBtn = q(".js-sp-cz-restore");
    if (reloadBtn) {
      reloadBtn.hidden = !away;
    }
    if (restoreBtn) {
      restoreBtn.hidden = away || !untrusted;
    }
  };
  const refreshAll = () => {
    renderAway();
    renderFirst();
    renderSelection();
    renderEditList();
    updateCounts();
  };
  const setAway = (isAway) => {
    away = isAway;
    if (isAway) {
      window.clearTimeout(ackTimer);
      pending = [];
    }
    refreshAll();
  };
  const setUntrusted = () => {
    trusted = false;
    untrusted = true;
    pending = [];
    window.clearTimeout(ackTimer);
    window.clearTimeout(logoTimer);
    trackError("untrusted_frame");
    refreshAll();
  };
  const hideToggleForever = () => {
    // 対応していないデモ（ブロックが無い・対象外）: 予約していた幅も返す。
    if (!everReady) {
      toggle.hidden = true;
      if (resetTop) {
        resetTop.hidden = true;
      }
    }
  };

  // 「デモのトップに戻す」/「入れた内容を再び表示する」: 親が既知の同一オリジンの URL を設定し直す（view_preview は再発火しない）。
  const reloadTop = () => {
    const src = iframe.getAttribute("data-sp-src");
    if (!src) {
      return;
    }
    expectLoad = true;
    trusted = true;
    navigating = true;
    untrusted = false;
    away = false;
    readyCount = 0;
    readySeen = false;
    awaitingReady = false;
    window.clearTimeout(graceTimer);
    track("reload_top");
    iframe.setAttribute("src", src);
    refreshAll();
  };

  iframe.addEventListener("load", () => {
    // 昇格前の about:blank の load は数えない。
    const srcAttr = iframe.getAttribute("src") || "";
    if (!srcAttr || srcAttr === "about:blank" || iframe.dataset.spPromoted !== "1") {
      return;
    }
    const parentLoad = expectLoad;
    expectLoad = false;
    if (!parentLoad) {
      // デモの中で移った（または外から移された）。以後は何も送らない。
      trusted = false;
      navigating = false;
      window.clearTimeout(ackTimer);
      window.clearTimeout(logoTimer);
    }
    window.clearTimeout(graceTimer);
    if (readySeen) {
      // この文書の ready は先に届いて処理済み（画像のある TOP では load より先に届く）。
      readySeen = false;
      return;
    }
    awaitingReady = true;
    graceTimer = window.setTimeout(() => {
      awaitingReady = false;
      if (!everReady) {
        hideToggleForever();
        return;
      }
      setAway(true);
    }, num("readyGraceMs", 1500));
  });

  const onReady = (d) => {
    if (awaitingReady) {
      awaitingReady = false;
      window.clearTimeout(graceTimer);
    } else {
      readySeen = true;
    }
    readyCount += 1;
    ready = {
      available: d.available === true,
      company_ready: d.company_ready === true,
      image_main: Number(d.image_main) > 0 ? 1 : 0,
      fonts_subset: d.fonts_subset === true,
    };
    if (!ready.available) {
      hideToggleForever();
      return;
    }
    everReady = true;
    toggle.removeAttribute("data-sp-cz-pending");
    toggle.hidden = false;
    if (!trusted || readyCount > 1) {
      // ready が届いた = いまはトップ（下層の案内のまま「トップページ以外」と出さない）。信頼しない枠として止め、「入れた内容を再び表示する」を出す。
      away = false;
      setUntrusted();
      return;
    }
    navigating = false;
    away = false;
    untrusted = false;
    if (!state.isEmpty()) {
      pushAssets();
      sendApply();
    }
    if (open) {
      sendMode();
      if (state.isEmpty()) {
        sendApply();
      }
      waitManifest();
    }
    refreshAll();
    scheduleLanding();
  };

  // ---- manifest → パネル ----
  const onManifest = (raw) => {
    window.clearTimeout(manifestTimer);
    manifest = sanitizeManifest(raw, srcOk);
    for (const t of manifest.texts) {
      if (!origTexts[t.ref]) {
        origTexts[t.ref] = t.orig.slice();
      }
    }
    // manifest は反映のたびに届く。入力中のカードは作り直さない（フォーカスと入力途中の文字を失わない）。
    renderAway();
    renderFirst();
    if (!card || !card.contains(doc.activeElement)) {
      renderSelection();
    }
    renderEditList();
    updateCounts();
  };

  const sectionOf = (secRef) => (manifest && secRef ? manifest.sections.find((s) => s.ref === secRef) : null);
  const sectionLabel = (secRef) => {
    const s = sectionOf(secRef);
    if (!s) {
      return "";
    }
    // 最初の画面（hero）は見出しの文ではなく「最初の画面」と呼ぶ（どこを選んでいるかが一目で分かる）。
    return s.fixed === "hero" ? msg("pick_first_view") : s.label || msg("pick_part");
  };
  const mainImage = () => (manifest ? manifest.images.find((i) => i.role === "main") : null);

  // ---- ロゴ画像（ヘッダー） ----
  const logoBox = q(".js-sp-cz-logo");
  const logoFile = q(".js-sp-cz-logo-file");
  const logoSlider = q(".js-sp-cz-logo-width");
  const logoOut = q(".js-sp-cz-logo-width-out");
  let logoTimer = 0;
  // スライダーの上限 = min(デモの実測（崩れない最大幅）, PC の上限 logoMaxWidthPx, 高さの上限 × 画像の縦横比)。
  // 実測と PC の上限はデモ側が manifest.logo.max に入れて返す。ここでも同じ規則で丸める（SP = プレビューの幅 768px 以下は実測どおり）。
  const logoMaxFor = (lg, w, h) => {
    let max = lg.max;
    if (iframe.clientWidth > 768) {
      max = Math.min(max, num("logoMaxWidthPx", 320));
    }
    if (w && h && lg.max_height) {
      max = Math.min(max, Math.floor((lg.max_height * w) / h));
    }
    return Math.max(1, max);
  };
  const logoBounds = () => {
    const lg = manifest && manifest.logo && manifest.logo.ready ? manifest.logo : null;
    if (!lg) {
      return null;
    }
    const a = state.data.logo ? state.asset(state.data.logo.asset) : null;
    const max = logoMaxFor(lg, a && a.width, a && a.height);
    return { min: Math.min(lg.min, max), max, orig: lg.width };
  };
  const renderLogo = () => {
    if (!logoBox) {
      return;
    }
    const b = logoBounds();
    const has = !!state.data.logo;
    if (logoFile) {
      logoFile.disabled = !usable() || !b;
    }
    const resetBtn = q(".js-sp-cz-logo-reset");
    if (resetBtn) {
      resetBtn.hidden = !has;
      resetBtn.disabled = !usable();
    }
    const revealBtn = q(".js-sp-cz-reveal[data-sp-kind='logo']");
    if (revealBtn) {
      revealBtn.hidden = !has || !usable();
    }
    const size = q(".js-sp-cz-logo-size");
    if (size) {
      size.hidden = !(has && b);
    }
    const note = q(".js-sp-cz-logo-note");
    if (manifest && !b) {
      note.hidden = false;
      note.classList.remove("is-warn");
      note.textContent = msg("logo_none");
      return;
    }
    if (has && b && logoSlider) {
      logoSlider.min = String(b.min);
      logoSlider.max = String(b.max);
      logoSlider.disabled = !usable() || b.min === b.max;
      const w = Math.min(b.max, Math.max(b.min, Number(state.data.logo.width) || b.max));
      // 範囲（端末切替・読み直しで変わる）の外にある幅は、範囲に丸めて送り直す。
      if (state.data.logo.width !== w) {
        state.data.logo.width = w;
        sendApply("logo_width");
      }
      if (doc.activeElement !== logoSlider) {
        logoSlider.value = String(w);
      }
      const px = fmt(msg("logo_px"), Number(logoSlider.value));
      logoSlider.setAttribute("aria-valuetext", px);
      if (logoOut) {
        logoOut.textContent = px;
      }
      const range = q(".js-sp-cz-logo-range");
      if (range) {
        range.textContent = fmt(msg("logo_range"), b.min, b.max);
      }
    }
    const over = !!(has && lastLogoApplied && lastLogoApplied.overflow);
    note.hidden = !over;
    note.classList.toggle("is-warn", over);
    note.textContent = over ? msg("logo_overflow") : "";
  };
  const imageError = (e) => {
    const code = e && e.code ? e.code : "image_decode";
    trackError(code);
    if (code === "image_type") {
      showToast(msg("err_image_type"));
    } else if (code === "image_too_large") {
      showToast(fmt(msg("err_image_size"), Math.round(num("imageMaxBytes", 10485760) / 1048576)));
    } else if (code === "image_pixels") {
      showToast(msg("err_image_pixels"));
    } else {
      showToast(msg("err_image_decode"));
    }
  };
  const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`.slice(0, 40);
  const chooseLogo = async (file) => {
    if (!file || !usable()) {
      return;
    }
    const lg = manifest.logo;
    if (!lg || !lg.ready) {
      return;
    }
    try {
      const out = await prepareImage(file, {
        maxBytes: num("imageMaxBytes", 10485760),
        maxPixels: isPhoneViewer() ? num("imageMaxPixelsSp", 20000000) : num("imageMaxPixels", 40000000),
        maxEdge: num("logoMaxEdge", 600),
        outMaxBytes: num("imageOutMaxBytes", 2097152),
        keepAlpha: true,
      });
      const prevBytes = state.data.logo ? (state.asset(state.data.logo.asset) || {}).bytes || 0 : 0;
      if (state.assetBytes() - prevBytes + out.bytes > num("imageTotalMaxBytes", 25165824)) {
        trackError("limit");
        showToast(msg("err_image_total"));
        return;
      }
      if (!usable()) {
        return;
      }
      const id = newId("l");
      const max = logoMaxFor(lg, out.width, out.height);
      // 最初の幅は元のロゴの幅（範囲に丸める）。
      const width = Math.max(Math.min(lg.min, max), Math.min(max, lg.width || max));
      state.putAsset(id, out);
      state.commit((d) => {
        d.logo = { asset: id, width };
      });
      post("asset", { id, blob: out.blob, kind: "logo" });
      sendApply("logo", "logo", "");
      renderLogo();
      updateCounts();
    } catch (e) {
      imageError(e);
    }
  };
  if (logoFile) {
    logoFile.addEventListener("change", () => {
      const f = logoFile.files && logoFile.files[0];
      logoFile.value = "";
      chooseLogo(f);
    });
  }
  const logoReset = q(".js-sp-cz-logo-reset");
  if (logoReset) {
    logoReset.addEventListener("click", () => {
      state.commit((d) => {
        d.logo = null;
      });
      lastLogoApplied = null;
      const st = q(".js-sp-cz-logo-status");
      if (st) {
        st.textContent = "";
      }
      sendApply("logo", "logo", "");
      renderLogo();
      updateCounts();
    });
  }
  if (logoSlider) {
    logoSlider.addEventListener("input", () => {
      const px = fmt(msg("logo_px"), Number(logoSlider.value));
      if (logoOut) {
        logoOut.textContent = px;
      }
      logoSlider.setAttribute("aria-valuetext", px);
      window.clearTimeout(logoTimer);
      // 数値 1 つだけなので、確定を待たずに反映する（50ms のデバウンス。デモ側でも範囲に丸める）。
      logoTimer = window.setTimeout(() => {
        const w = Math.round(Number(logoSlider.value));
        if (!state.data.logo || !Number.isFinite(w) || state.data.logo.width === w) {
          return;
        }
        state.data.logo.width = w;
        sendApply("logo_width");
      }, num("logoDebounceMs", 50));
    });
    logoSlider.addEventListener("change", () => {
      if (state.data.logo) {
        track("logo_width");
        announce(fmt(msg("logo_px"), Number(logoSlider.value)));
      }
    });
  }

  // ---- 会社名（ヘッダー以外） ----
  const companyInput = q(".js-sp-cz-company");
  const companyStatus = q(".js-sp-cz-company-status");
  const companyNote = q(".js-sp-cz-company-note");
  const companyReset = q(".js-sp-cz-company-reset");
  const companyReveal = q(".js-sp-cz-reveal[data-sp-kind='company']");
  const confirmCompany = () => {
    if (!companyInput || !usable() || !manifest.company) {
      return;
    }
    const v = companyInput.value.trim().slice(0, num("companyMaxChars", 30));
    if (v === state.data.company) {
      return;
    }
    if (v && v.length < num("companyMinChars", 2)) {
      companyStatus.textContent = fmt(msg("company_min"), num("companyMinChars", 2));
      return;
    }
    state.commit((d) => {
      d.company = v;
    });
    sendApply("company", "company", "");
    renderFirst();
    updateCounts();
    // 確定後は自動でたたむ（置き換えた場所がパネルに隠れない。docs/22 §3.1）。
    if (v && !isPhoneViewer()) {
      setCollapsed(true);
    }
  };
  if (companyInput) {
    companyInput.addEventListener("keydown", (e) => {
      // 変換中の Enter（IME の確定）では反映しない。
      if (e.key === "Enter" && !e.isComposing && e.keyCode !== 229) {
        e.preventDefault();
        confirmCompany();
        companyInput.blur();
      }
    });
    companyInput.addEventListener("blur", confirmCompany);
  }
  if (companyReset) {
    companyReset.addEventListener("click", () => {
      if (companyInput) {
        companyInput.value = "";
      }
      state.commit((d) => {
        d.company = "";
      });
      companyStatus.textContent = "";
      sendApply("company", "company", "");
      renderFirst();
      updateCounts();
    });
  }
  if (companyReveal) {
    companyReveal.addEventListener("click", () => {
      reveal("company");
      track("reveal");
    });
  }
  const logoReveal = q(".js-sp-cz-reveal[data-sp-kind='logo']");
  if (logoReveal) {
    logoReveal.addEventListener("click", () => {
      reveal("logo");
      track("reveal");
    });
  }

  // ---- 写真 ----
  const chooseImage = async (ref, file) => {
    if (!file || !usable()) {
      return;
    }
    if (Object.keys(state.data.images).length >= num("imageMaxCount", 20) && !state.data.images[ref]) {
      trackError("limit");
      showToast(fmt(msg("err_image_count"), num("imageMaxCount", 20)));
      return;
    }
    try {
      const out = await prepareImage(file, {
        maxBytes: num("imageMaxBytes", 10485760),
        maxPixels: isPhoneViewer() ? num("imageMaxPixelsSp", 20000000) : num("imageMaxPixels", 40000000),
        maxEdge: num("imageMaxEdge", 1600),
        outMaxBytes: num("imageOutMaxBytes", 2097152),
        keepAlpha: false,
      });
      if (state.assetBytes() + out.bytes > num("imageTotalMaxBytes", 25165824)) {
        trackError("limit");
        showToast(msg("err_image_total"));
        return;
      }
      if (!usable()) {
        return;
      }
      const id = newId("a");
      state.putAsset(id, out);
      state.commit((d) => {
        d.images[ref] = id;
      });
      post("asset", { id, blob: out.blob, kind: "image" });
      const info = manifest.images.find((i) => i.ref === ref);
      sendApply("image", "image", sectionKey(info ? info.section : ""));
      renderFirst();
      renderSelection();
      refreshSectionMarks();
      updateCounts();
    } catch (e) {
      imageError(e);
    }
  };
  const resetImage = (ref) => {
    const info = manifest ? manifest.images.find((i) => i.ref === ref) : null;
    state.commit((d) => {
      delete d.images[ref];
    });
    sendApply("", "image", sectionKey(info ? info.section : ""));
    renderFirst();
    renderSelection();
    refreshSectionMarks();
    updateCounts();
  };
  const mainFile = q(".js-sp-cz-main-file");
  if (mainFile) {
    mainFile.addEventListener("change", () => {
      const f = mainFile.files && mainFile.files[0];
      mainFile.value = "";
      const main = mainImage();
      if (main) {
        chooseImage(main.ref, f);
      }
    });
  }
  const mainReset = q(".js-sp-cz-main-image-reset");
  if (mainReset) {
    mainReset.addEventListener("click", () => {
      const main = mainImage();
      if (main) {
        resetImage(main.ref);
      }
    });
  }
  const ratioText = (w, h) => {
    const r = ratioOf(w, h);
    return r.ratio ? `${r.ratio}（${msg(`ratio_${r.orientation}`)}）` : "";
  };
  const imageHelp = (info) => {
    const parts = [];
    const r = ratioText(info.width, info.height);
    if (r) {
      parts.push(fmt(msg("image_ratio"), r));
    }
    if (info.slides > 1) {
      parts.push(fmt(msg("image_slides"), info.slides));
    }
    return parts.join(" ");
  };

  // ---- 色（既存のカラー切替の案を写し、押したら上部バーのボタンへ委ねる。live-colors.js は変えない） ----
  const colorWrap = q(".js-sp-cz-colors");
  const colorNone = q(".js-sp-cz-color-none");
  const topColorList = root.querySelector(".js-sp-live-colors [data-sp-colors-list]");
  const topColorButtons = () => (topColorList ? Array.from(topColorList.querySelectorAll(".js-sp-live-color")) : []);
  const currentColor = () => {
    const b = topColorButtons().find((x) => x.getAttribute("aria-pressed") === "true");
    return b ? b.getAttribute("data-sp-color") || "base" : "base";
  };
  const colorLabel = () => {
    const key = currentColor();
    if (key === "base") {
      return "";
    }
    const b = topColorButtons().find((x) => x.getAttribute("data-sp-color") === key);
    return b ? b.title || key : key;
  };
  let colorSig = "";
  const renderColors = () => {
    if (!colorWrap) {
      return;
    }
    const tops = topColorButtons();
    if (colorNone) {
      colorNone.hidden = tops.length > 0;
    }
    const sig = tops.map((b) => b.getAttribute("data-sp-color")).join(",");
    if (sig !== colorSig) {
      colorSig = sig;
      colorWrap.textContent = "";
      for (const top of tops) {
        const key = top.getAttribute("data-sp-color") || "";
        const chipTop = top.querySelector(".c-color-toggle__chip");
        const b = button(
          "p-live-customize__color",
          "",
          () => {
            const t = topColorButtons().find((x) => x.getAttribute("data-sp-color") === key);
            if (t) {
              t.click();
            }
          },
          { "aria-pressed": "false", "data-sp-color-key": key }
        );
        const chip = el("span", "p-live-customize__chip");
        chip.setAttribute("aria-hidden", "true");
        if (chipTop) {
          // 見本の色は live-colors.js が検査済みの #hex を入れたカスタムプロパティを写すだけ。
          chip.style.setProperty("--sp-chip-1", chipTop.style.getPropertyValue("--sp-chip-1"));
          chip.style.setProperty("--sp-chip-2", chipTop.style.getPropertyValue("--sp-chip-2"));
        }
        b.append(chip, el("span", "p-live-customize__color-label", key === "base" && top.title ? top.title : top.title || key));
        colorWrap.append(b);
      }
    }
    const cur = currentColor();
    colorWrap.querySelectorAll("button").forEach((b) => {
      const on = b.getAttribute("data-sp-color-key") === cur;
      b.setAttribute("aria-pressed", on ? "true" : "false");
      b.classList.toggle("is-active", on);
      b.disabled = !everReady;
    });
  };
  if (topColorList && typeof MutationObserver === "function") {
    new MutationObserver(() => {
      renderColors();
      updateCounts();
    }).observe(topColorList, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-pressed"] });
  }

  // ---- まず、ここから ----
  const renderFirst = () => {
    if (companyInput) {
      const has = !!(manifest && manifest.company);
      companyInput.disabled = !usable() || !has;
      if (manifest) {
        companyInput.placeholder = manifest.company || "";
      }
      if (doc.activeElement !== companyInput) {
        companyInput.value = state.data.company || "";
      }
      if (companyReset) {
        companyReset.hidden = !state.data.company;
        companyReset.disabled = !usable();
      }
      if (companyReveal && !state.data.company) {
        companyReveal.hidden = true;
      }
      if (manifest && !has && companyNote) {
        companyNote.hidden = false;
        companyNote.classList.remove("is-warn");
        companyNote.textContent = msg("company_none");
      }
    }
    const box = q(".js-sp-cz-main-image");
    const main = mainImage();
    if (box) {
      box.hidden = !main;
      if (main) {
        const help = q(".js-sp-cz-main-image-ratio");
        if (help) {
          help.textContent = imageHelp(main);
        }
        if (mainReset) {
          mainReset.hidden = !state.data.images[main.ref];
          mainReset.disabled = !usable();
        }
      }
    }
    if (mainFile) {
      mainFile.disabled = !usable() || !main;
    }
    renderLogo();
    renderColors();
  };

  // ---- 選んでいる場所 ----
  const card = q(".js-sp-cz-sel-card");
  const cardHome = card ? { parent: card.parentNode, next: card.nextSibling } : null;
  const pickHint = q(".js-sp-cz-pick-hint");
  const textKindLabel = (t) => {
    if (/^h[12]$/.test(t.tag)) {
      return msg("pick_heading");
    }
    if (/^(h[3-6]|b|strong|dt|em|th)$/.test(t.tag)) {
      return msg("pick_subheading");
    }
    if (/^(a|button)$/.test(t.tag)) {
      return msg("pick_link");
    }
    return msg("pick_body");
  };
  const textLimit = (t) => {
    const base = origTexts[t.ref] || t.orig;
    const len = base.join("").length || 10;
    return t.fv
      ? Math.min(num("textMaxCharsHero", 120), Math.max(len + 4, Math.ceil(len * num("textScaleHero", 1.3))))
      : Math.min(num("textMaxChars", 300), Math.max(len + 10, Math.ceil(len * num("textScale", 2))));
  };
  const sizeLabel = (k) => msg(`text_size_${k}`);
  const selectRef = (ref, kind) => {
    selected = { ref, kind };
    if (kind === "text" && manifest) {
      textCursor = manifest.texts.findIndex((t) => t.ref === ref);
    }
    renderSelection();
  };
  // 選んだ場所の入力欄（カード）までパネルを送り、短く強調する。プレビューで選んでも一覧で選んでも同じ。
  let flashTimer = 0;
  const revealCard = () => {
    if (!card || card.hidden) {
      return;
    }
    const scroller = card.closest(".js-sp-cz-body");
    if (scroller) {
      // 一覧の中なら、選んだ行（入力欄のすぐ上）が見える位置まで送る。
      const prev = card.previousElementSibling;
      const anchor = prev && prev.classList.contains("p-live-customize__row") ? prev : card;
      const top = anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 12;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      scroller.scrollTo({ top: Math.max(0, top), behavior: reduce ? "auto" : "smooth" });
    }
    card.classList.remove("is-flash");
    card.getBoundingClientRect();
    card.classList.add("is-flash");
    window.clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => card.classList.remove("is-flash"), 1400);
  };
  // 入力欄（カード）の置き場所: 一覧に選んだ行があれば、その行のすぐ下（パーツを開いて）に置く。
  // 次の場所を選ぶときに一覧の中で迷わないようにするため（2026-10-23 人間指摘）。
  // SP で写真を選んだときは写真タブ、一覧に行が無いとき（パーツ選択など）は元の位置。
  const rowFor = (ref) => {
    if (!editList || !ref) {
      return null;
    }
    const rows = editList.querySelectorAll(".p-live-customize__row");
    for (const r of rows) {
      if (r.getAttribute("data-sp-ref") === ref) {
        return r;
      }
    }
    return null;
  };
  const placeCard = () => {
    if (!card || !cardHome) {
      return;
    }
    const slot = q(".js-sp-cz-photo-slot");
    const toPhoto = !!slot && isPhoneViewer() && !!selected && selected.kind === "image";
    const row = !toPhoto && selected ? rowFor(selected.ref) : null;
    if (toPhoto) {
      if (card.parentNode !== slot) {
        slot.append(card);
      }
    } else if (row) {
      const li = row.parentNode;
      const det = row.closest("details");
      if (det && !det.open) {
        det.open = true;
      }
      if (card.parentNode !== li || li.lastChild !== card) {
        li.append(card);
      }
    } else if (card.parentNode !== cardHome.parent) {
      cardHome.parent.insertBefore(card, cardHome.next);
    }
    const hint = q(".js-sp-cz-photo-hint");
    if (hint) {
      hint.hidden = toPhoto;
    }
  };
  const cardKind = (secRef, kindText) => {
    const label = sectionLabel(secRef);
    return el("p", "p-live-customize__card-kind", `${msg("pick_selected")}: ${label ? `${label} ／ ` : ""}${kindText}`);
  };
  const confirmText = (t, ta, max) => {
    if (!usable()) {
      return;
    }
    const rows = ta.value.split("\n").map((r) => r.trim());
    while (rows.length > 1 && rows[rows.length - 1] === "") {
      rows.pop();
    }
    if (rows.join("").length > max) {
      showToast(fmt(msg(t.fv ? "text_fv_limit" : "text_limit"), max));
      return;
    }
    const current = state.data.texts[t.ref] || origTexts[t.ref] || t.orig;
    if (JSON.stringify(rows) === JSON.stringify(current)) {
      return;
    }
    state.commit((d) => {
      if (JSON.stringify(rows) === JSON.stringify(origTexts[t.ref] || t.orig)) {
        delete d.texts[t.ref];
      } else {
        d.texts[t.ref] = rows;
      }
    });
    sendApply("text", "text", sectionKey(t.section));
    refreshSectionMarks();
    updateCounts();
    const resetBtn = card.querySelector(".js-sp-cz-text-reset");
    if (resetBtn) {
      resetBtn.hidden = !state.data.texts[t.ref];
    }
  };
  const renderTextCard = (wrap, t) => {
    const sec = sectionOf(t.section);
    const fv = t.fv && !(sec && sec.fixed === "hero") ? `（${msg("pick_first_view")}）` : "";
    wrap.append(cardKind(t.section, `${textKindLabel(t)}${fv}`));
    const max = textLimit(t);
    const taId = `sp-cz-text-${t.ref.replace(/[^a-z0-9]/gi, "-")}`;
    const ta = /** @type {HTMLTextAreaElement} */ (el("textarea", "p-live-customize__textarea"));
    ta.id = taId;
    ta.rows = Math.min(6, Math.max(2, t.rows.length + 1));
    ta.maxLength = max + Math.max(0, t.rows.length - 1); // 改行の分は数えない
    ta.value = (state.data.texts[t.ref] || origTexts[t.ref] || t.orig).join("\n");
    ta.disabled = !usable();
    ta.setAttribute("aria-label", msg("pick_selected"));
    const counter = el("p", "p-live-customize__counter");
    counter.setAttribute("aria-live", "off");
    const updateCounter = () => {
      const n = ta.value.replace(/\n/g, "").length;
      counter.textContent = fmt(msg("text_counter"), n, max);
      counter.classList.toggle("has-error", n > max);
    };
    updateCounter();
    ta.addEventListener("input", updateCounter);
    ta.addEventListener("blur", () => confirmText(t, ta, max));
    ta.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.isComposing) {
        e.preventDefault();
        confirmText(t, ta, max);
      }
    });
    wrap.append(ta, counter);
    const help = el("p", "p-live-customize__help", fmt(msg(t.fv ? "text_fv_limit" : "text_limit"), max));
    if (t.rows.length > 1) {
      help.textContent += ` ${msg("text_rows_note")}`;
    }
    ta.setAttribute("aria-describedby", `${taId}-help`);
    help.id = `${taId}-help`;
    wrap.append(help);
    const row = el("div", "p-live-customize__actions");
    const applyBtn = button("c-button c-button--sm js-sp-cz-text-apply", msg("text_apply"), () => {
      confirmText(t, ta, max);
      if (isPhoneViewer()) {
        ta.blur();
      }
    });
    applyBtn.disabled = !usable();
    const resetBtn = button("p-live-customize__text-button js-sp-cz-text-reset", msg("text_reset"), () => {
      state.commit((d) => {
        delete d.texts[t.ref];
      });
      sendApply("", "text", sectionKey(t.section));
      renderSelection();
      refreshSectionMarks();
      updateCounts();
    });
    resetBtn.hidden = !state.data.texts[t.ref];
    resetBtn.disabled = !usable();
    row.append(applyBtn, resetBtn);
    wrap.append(row);

    // 文字の大きさ S / M / L（M = 元の大きさ。倍率はデモ側の定数表が正）。
    const cur = state.data.text_sizes[t.ref] || "m";
    const fs = el("fieldset", "p-live-customize__sizes");
    fs.append(el("legend", "p-live-customize__sizes-legend", msg("text_size_label")));
    const group = el("div", "p-live-customize__size-group");
    for (const k of SIZE_KEYS) {
      const lab = el("label", "p-live-customize__size");
      const input = /** @type {HTMLInputElement} */ (el("input", "p-live-customize__size-input"));
      input.type = "radio";
      input.name = "sp-cz-text-size";
      input.value = k;
      input.checked = cur === k;
      input.disabled = !usable();
      input.addEventListener("change", () => {
        if (!input.checked || (state.data.text_sizes[t.ref] || "m") === k) {
          return;
        }
        state.commit((d) => {
          if (k === "m") {
            delete d.text_sizes[t.ref];
          } else {
            d.text_sizes[t.ref] = k;
          }
        });
        sendApply("text_size", "text_size", sectionKey(t.section));
        const note = card.querySelector(".js-sp-cz-size-note");
        if (note) {
          note.hidden = true;
        }
        refreshSectionMarks();
        updateCounts();
      });
      lab.append(input, el("span", "p-live-customize__size-text", k.toUpperCase()));
      lab.title = sizeLabel(k);
      input.setAttribute("aria-label", sizeLabel(k));
      group.append(lab);
    }
    fs.append(group);
    wrap.append(fs);
    const note = el("p", "p-live-customize__help is-warn js-sp-cz-size-note", msg("text_size_note"));
    const warn = lastTextSizes[t.ref];
    note.hidden = !(cur !== "m" && warn && (warn.overflow || warn.more_lines));
    wrap.append(note);
    if (ready && ready.fonts_subset) {
      wrap.append(el("p", "p-live-customize__help", msg("company_font_note")));
    }
  };
  const sharedPlaceLabel = (r) => {
    const o = manifest.images.find((x) => x.ref === r);
    return o ? sectionLabel(o.section) : "";
  };
  const renderImageCard = (wrap, info) => {
    wrap.append(cardKind(info.section, msg("pick_image")));
    const help = imageHelp(info);
    if (help) {
      wrap.append(el("p", "p-live-customize__help", help));
    }
    const row = el("div", "p-live-customize__actions");
    const lab = el("label", "c-button c-button--neutral c-button--sm p-live-customize__file", msg("image_replace"));
    const input = /** @type {HTMLInputElement} */ (el("input", "u-visually-hidden js-sp-cz-card-file"));
    input.type = "file";
    input.accept = ACCEPT;
    input.disabled = !usable();
    input.addEventListener("change", () => {
      const f = input.files && input.files[0];
      input.value = "";
      chooseImage(info.ref, f);
    });
    lab.append(input);
    row.append(lab);
    if (state.data.images[info.ref]) {
      const r = button("p-live-customize__text-button", msg("image_reset"), () => resetImage(info.ref));
      r.disabled = !usable();
      row.append(r);
    }
    wrap.append(row, el("p", "p-live-customize__help", msg("image_help")));
    // 同じ元画像を使っている場所にも、選んだ写真を反映できる（出現箇所ごとが既定。docs/22 §12-10 (a)）。
    const shared = info.shared_with.filter((r) => manifest.images.some((x) => x.ref === r));
    if (shared.length) {
      const places = [...new Set(shared.map(sharedPlaceLabel).filter(Boolean))].join("・");
      wrap.append(el("p", "p-live-customize__help", `${fmt(msg("image_shared"), shared.length)}${places ? `（${places}）` : ""}`));
      const id = state.data.images[info.ref];
      if (id) {
        const waiting = shared.filter((r) => state.data.images[r] !== id);
        if (waiting.length) {
          const b = button("c-button c-button--neutral c-button--sm js-sp-cz-image-shared", msg("image_shared_apply"), () => {
            state.commit((d) => {
              for (const r of waiting) {
                d.images[r] = id;
              }
            });
            sendApply("", "image", sectionKey(info.section));
            renderFirst();
            renderSelection();
            refreshSectionMarks();
            updateCounts();
            const done = fmt(msg("image_shared_done"), waiting.length);
            showToast(done);
            announce(done);
          });
          b.disabled = !usable();
          wrap.append(b);
        } else {
          wrap.append(el("p", "p-live-customize__help", msg("image_shared_all")));
        }
      }
    }
  };
  // パーツ / 項目の余白を押したとき（A1 は構成の操作を出さず、そのパーツの文章・写真へ案内するだけ）。
  const renderPartCard = (wrap, ref, kind) => {
    const secRef = kind === "section" ? ref : ref.replace(/~.*$/, "").split(":").slice(0, 2).join(":");
    wrap.append(cardKind(secRef, kind === "item" ? msg("pick_item") : msg("pick_part")));
    wrap.append(el("p", "p-live-customize__help", msg("pick_part_help")));
    const texts = manifest.texts.filter((t) => (kind === "item" ? t.item === ref : t.section === secRef));
    if (texts.length) {
      const b = button("c-button c-button--neutral c-button--sm", fmt(msg("pick_part_texts"), texts.length), () => {
        selectRef(texts[0].ref, "text");
        focusRef(texts[0].ref);
      });
      b.disabled = !usable();
      wrap.append(b);
    }
  };
  const renderSelection = () => {
    if (!card) {
      return;
    }
    placeCard();
    card.textContent = "";
    if (pickHint) {
      pickHint.hidden = !!selected;
    }
    const info = selected && manifest ? selected : null;
    if (!info) {
      card.hidden = true;
      markEditList();
      return;
    }
    const wrap = el("div", "p-live-customize__card");
    if (info.kind === "text") {
      const t = manifest.texts.find((x) => x.ref === info.ref);
      if (!t) {
        card.hidden = true;
        return;
      }
      renderTextCard(wrap, t);
    } else if (info.kind === "image") {
      const i = manifest.images.find((x) => x.ref === info.ref);
      if (!i) {
        card.hidden = true;
        return;
      }
      renderImageCard(wrap, i);
    } else {
      renderPartCard(wrap, info.ref, info.kind);
    }
    card.append(wrap);
    card.hidden = false;
    markEditList();
  };

  // ---- 文章・写真を見る（クリックしなくても選べる一覧。パーツごとに折りたたみ） ----
  const editList = q(".js-sp-cz-edit-list");
  const editOpen = new Set();
  let editSig = "";
  const sectionChanges = (secRef) => {
    const d = state.data;
    const inSec = (ref, list) => {
      const x = list.find((y) => y.ref === ref);
      return !!x && x.section === secRef;
    };
    const texts = [...new Set([...Object.keys(d.texts), ...Object.keys(d.text_sizes)])].filter((r) => manifest && inSec(r, manifest.texts));
    const images = Object.keys(d.images).filter((r) => manifest && inSec(r, manifest.images));
    return { texts, images, total: texts.length + images.length };
  };
  const resetSection = (secRef) => {
    const c = sectionChanges(secRef);
    if (!c.total) {
      return;
    }
    state.commit((d) => {
      for (const r of c.texts) {
        delete d.texts[r];
        delete d.text_sizes[r];
      }
      for (const r of c.images) {
        delete d.images[r];
      }
    });
    sendApply("", "reset", sectionKey(secRef));
    renderFirst();
    renderSelection();
    refreshSectionMarks();
    updateCounts();
    const done = fmt(msg("section_reset_done"), sectionLabel(secRef));
    showToast(done);
    announce(done);
  };
  const pickFromList = (ref, kind) => {
    selectRef(ref, kind);
    focusRef(ref);
    if (isPhoneViewer()) {
      showTabFor(kind);
      revealCard();
      return;
    }
    revealCard();
    const ta = card.querySelector("textarea");
    if (ta) {
      ta.focus({ preventScroll: true });
    }
  };
  const renderEditList = () => {
    if (!editList) {
      return;
    }
    if (!manifest) {
      editList.textContent = "";
      editSig = "";
      return;
    }
    const groups = manifest.sections
      .map((sec) => ({
        sec,
        images: manifest.images.filter((i) => i.section === sec.ref),
        texts: manifest.texts.filter((t) => t.section === sec.ref),
      }))
      .filter((g) => g.images.length + g.texts.length > 0);
    // 中身（ref・元の文・サムネ）が変わったときだけ作り直す（反映のたびに作り直すとフォーカスと開閉が飛ぶ）。
    const sig = JSON.stringify(groups.map((g) => [g.sec.ref, g.sec.label, g.images.map((i) => [i.ref, i.src, i.slides]), g.texts.map((t) => [t.ref, (origTexts[t.ref] || t.orig).join("|")])]));
    if (sig !== editSig) {
      editSig = sig;
      // 一覧の中に置いた入力欄を、作り直しで消さないよう元の位置へ戻してから空にする（最後に placeCard で置き直す）。
      if (card && cardHome && editList.contains(card)) {
        cardHome.parent.insertBefore(card, cardHome.next);
      }
      editList.textContent = "";
      for (const g of groups) {
        const label = sectionLabel(g.sec.ref);
        const det = el("details", "p-live-customize__part");
        det.setAttribute("data-sp-ref", g.sec.ref);
        det.open = editOpen.has(g.sec.ref);
        det.addEventListener("toggle", () => {
          if (det.open) {
            editOpen.add(g.sec.ref);
          } else {
            editOpen.delete(g.sec.ref);
          }
        });
        const sum = el("summary", "p-live-customize__part-head");
        const counts = [g.texts.length ? fmt(msg("pick_list_texts"), g.texts.length) : "", g.images.length ? fmt(msg("pick_list_images"), g.images.length) : ""].filter(Boolean).join("・");
        sum.append(el("span", "p-live-customize__part-label", label), el("span", "p-live-customize__part-mark js-sp-cz-part-mark", msg("section_changed")), el("span", "p-live-customize__part-count", counts));
        det.append(sum);
        const resetBtn = button("p-live-customize__text-button js-sp-cz-section-reset", msg("section_reset"), () => resetSection(g.sec.ref), { "data-sp-ref": g.sec.ref });
        det.append(resetBtn);
        const ul = el("ul", "p-live-customize__rows");
        for (const i of g.images) {
          const li = el("li");
          const where = `${msg("pick_image")}${i.role === "main" ? `（${msg("image_main_label") || msg("pick_first_view")}）` : ""}${i.slides > 1 ? fmt(msg("memo_slides"), i.slides) : ""}${i.alt ? ` ／ ${i.alt}` : ""}`;
          const b = button("p-live-customize__row js-sp-cz-edit-image", "", () => pickFromList(i.ref, "image"), { "data-sp-ref": i.ref, "aria-label": fmt(msg("pick_choose_image"), label, where), "aria-pressed": "false" });
          const th = /** @type {HTMLImageElement} */ (el("img", "p-live-customize__thumb"));
          th.alt = "";
          th.width = 40;
          th.height = 30;
          th.loading = "lazy";
          th.decoding = "async";
          if (i.src) {
            th.src = i.src;
          } else {
            th.classList.add("is-empty");
          }
          b.append(th, el("span", "p-live-customize__row-text", where));
          li.append(b);
          ul.append(li);
        }
        for (const t of g.texts) {
          const li = el("li");
          const head = headOf(origTexts[t.ref] || t.orig);
          const b = button("p-live-customize__row js-sp-cz-edit-text", "", () => pickFromList(t.ref, "text"), { "data-sp-ref": t.ref, "aria-label": fmt(msg("pick_choose_text"), label, head), "aria-pressed": "false" });
          const ico = el("span", "p-live-customize__row-icon", "✎");
          ico.setAttribute("aria-hidden", "true");
          b.append(ico, el("span", "p-live-customize__row-text", head));
          li.append(b);
          ul.append(li);
        }
        det.append(ul);
        editList.append(det);
      }
    }
    // 一覧の行とパーツの戻すボタンだけ（一覧の中に置いた入力欄のボタンは入力欄側で管理する）。
    editList.querySelectorAll(".p-live-customize__row, .js-sp-cz-section-reset").forEach((b) => {
      b.disabled = !usable();
    });
    markEditList();
    refreshSectionMarks();
    placeCard();
  };
  const refreshSectionMarks = () => {
    if (!editList) {
      return;
    }
    editList.querySelectorAll(".js-sp-cz-section-reset").forEach((b) => {
      const n = manifest ? sectionChanges(b.getAttribute("data-sp-ref")).total : 0;
      b.hidden = n === 0;
      const det = b.closest("details");
      if (det) {
        det.classList.toggle("has-change", n > 0);
      }
    });
  };
  const markEditList = () => {
    if (!editList) {
      return;
    }
    editList.querySelectorAll(".p-live-customize__row").forEach((b) => {
      const on = !!selected && b.getAttribute("data-sp-ref") === selected.ref;
      b.classList.toggle("is-selected", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });
    if (selected && (selected.kind === "text" || selected.kind === "image")) {
      const row = Array.from(editList.querySelectorAll(".p-live-customize__row")).find((b) => b.getAttribute("data-sp-ref") === selected.ref);
      const det = row ? row.closest("details") : null;
      if (det && !det.open) {
        det.open = true;
      }
    }
  };
  const stepText = (dir) => {
    if (!manifest || !manifest.texts.length || !usable()) {
      return;
    }
    textCursor = (textCursor + dir + manifest.texts.length) % manifest.texts.length;
    const t = manifest.texts[textCursor];
    selectRef(t.ref, "text");
    focusRef(t.ref);
    if (isPhoneViewer()) {
      return; // SP はキーボードを勝手に出さない
    }
    const ta = card.querySelector("textarea");
    if (ta) {
      ta.focus({ preventScroll: true });
    }
  };
  const prevBtn = q(".js-sp-cz-prev-text");
  const nextBtn = q(".js-sp-cz-next-text");
  if (prevBtn) {
    prevBtn.addEventListener("click", () => stepText(-1));
  }
  if (nextBtn) {
    nextBtn.addEventListener("click", () => stepText(1));
  }

  // ---- 変更の件数・元に戻す（パネル下端の操作バー / 上部バー / パーツ単位） ----
  const resetBtn = q(".js-sp-cz-reset");
  const countEl = q(".js-sp-cz-change-count");
  const toggleBadge = q(".js-sp-cz-toggle-badge");
  const toggleCount = q(".js-sp-cz-toggle-count");
  const railBadge = q(".js-sp-cz-rail-badge");
  const entryLabel = q(".js-sp-cz-entry-label");
  const entryBadge = q(".js-sp-cz-entry-badge");
  const changeCount = () => {
    const d = state.data;
    let n = (d.company ? 1 : 0) + (d.logo ? 1 : 0) + (currentColor() !== "base" ? 1 : 0) + Object.keys(d.images).length;
    n += new Set([...Object.keys(d.texts), ...Object.keys(d.text_sizes)]).size;
    return n;
  };
  const updateCounts = () => {
    const n = changeCount();
    if (countEl) {
      countEl.textContent = n ? fmt(msg("change_count"), n) : msg("change_none");
      countEl.classList.toggle("has-change", n > 0);
    }
    if (resetBtn) {
      resetBtn.disabled = n === 0 || !usable();
      if (n === 0) {
        disarm(resetBtn);
      }
    }
    if (resetTop) {
      resetTop.hidden = n === 0 || !everReady || !canSend();
      if (n === 0) {
        disarm(resetTop);
      }
    }
    for (const badge of [toggleBadge, railBadge]) {
      if (badge) {
        badge.hidden = !n;
        badge.textContent = n ? String(n) : "";
      }
    }
    if (toggleCount) {
      toggleCount.textContent = n ? fmt(msg("toggle_count"), n) : "";
    }
    updateEntry(n);
  };

  // 閉じている間の入口（PC の縦長タブ / SP の浮いたボタン）。
  let closedOnce = false;
  const updateEntry = (count) => {
    if (!entry) {
      return;
    }
    const n = typeof count === "number" ? count : changeCount();
    const show = everReady && canSend() && !open;
    entry.hidden = !show;
    root.classList.toggle("has-cz-entry", show);
    if (!show) {
      entry.classList.remove("is-compact");
    }
    if (entryLabel) {
      entryLabel.textContent = n ? msg("entry_more") : msg("entry");
    }
    if (entryBadge) {
      entryBadge.hidden = !n;
      entryBadge.textContent = n ? String(n) : "";
    }
    entry.setAttribute("aria-label", n ? fmt(msg("entry_aria_more"), n) : msg("entry"));
  };
  const pulse = (node, cls) => {
    if (!node) {
      return;
    }
    node.classList.remove(cls);
    void node.offsetWidth; // 付け直してアニメーションをもう一度起こす（reduced-motion は CSS で無し）
    node.classList.add(cls);
  };
  const afterClose = () => {
    updateEntry();
    if (!entry || entry.hidden) {
      return;
    }
    pulse(entry, "is-pulse-once");
    if (!closedOnce) {
      closedOnce = true;
      showToast(isPhoneViewer() ? msg("entry_toast_sp") : msg("entry_toast_pc"), null, 3000);
    }
  };

  // 2 段の確認: 1 回目でラベルが変わり、一定時間で戻る。2 回目で実行（ボタンごとに独立）。
  const armTimers = new Map();
  const disarm = (b) => {
    if (!b || !armTimers.has(b)) {
      return;
    }
    window.clearTimeout(armTimers.get(b));
    armTimers.delete(b);
    b.classList.remove("is-armed");
    b.textContent = b.getAttribute("data-sp-cz-label") || "";
  };
  const armOrRun = (b) => {
    if (!armTimers.has(b)) {
      b.textContent = msg("reset_confirm");
      b.classList.add("is-armed");
      armTimers.set(
        b,
        window.setTimeout(() => disarm(b), num("resetArmMs", 4000))
      );
      announce(msg("reset_confirm"));
      return;
    }
    disarm(b);
    resetAll();
  };
  const resetAll = () => {
    disarm(resetBtn);
    disarm(resetTop);
    state.reset();
    selected = null;
    lastLogoApplied = null;
    lastTextSizes = Object.create(null);
    if (currentColor() !== "base") {
      const base = topColorButtons().find((x) => x.getAttribute("data-sp-color") === "base");
      if (base) {
        base.click();
      }
    }
    for (const s of [q(".js-sp-cz-company-status"), q(".js-sp-cz-logo-status")]) {
      if (s) {
        s.textContent = "";
      }
    }
    if (companyNote && manifest && manifest.company) {
      companyNote.hidden = true;
    }
    if (companyInput) {
      companyInput.value = "";
    }
    sendApply("", "reset", "");
    refreshAll();
    showToast(msg("reset_done"));
    announce(msg("reset_done"));
  };
  if (resetBtn) {
    resetBtn.addEventListener("click", () => armOrRun(resetBtn));
  }
  if (resetTop) {
    resetTop.addEventListener("click", () => armOrRun(resetTop));
  }

  // ---- メモ ----
  const memoText = () =>
    buildMemo({ msg, title: productTitle, url: pageUrl, data: state.data, manifest, origTexts, colorLabel: colorLabel(), sectionLabel });
  const copyMemo = async () => {
    const full = memoText();
    const max = num("memoMaxChars", 6000);
    const ok = await copyText(full.length > max ? full.slice(0, max) : full);
    if (!ok) {
      showToast(msg("export_failed"));
      return;
    }
    const done = full.length > max ? msg("export_trimmed") : msg("exported");
    showToast(done);
    announce(done);
    track("export");
  };
  const exportCopy = q(".js-sp-cz-export-copy");
  const exportFile = q(".js-sp-cz-export-file");
  const exportShare = q(".js-sp-cz-export-share");
  if (exportCopy) {
    exportCopy.addEventListener("click", copyMemo);
  }
  if (exportFile) {
    exportFile.addEventListener("click", () => {
      const blob = new Blob([memoText()], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = doc.createElement("a");
      a.href = url;
      a.download = `sitepick-memo-${itemId || "site"}.txt`;
      doc.body.append(a);
      a.click();
      a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      track("export");
    });
  }
  if (exportShare) {
    exportShare.addEventListener("click", async () => {
      try {
        await navigator.share({ text: memoText() });
        track("export");
      } catch (e) {
        if (!e || e.name !== "AbortError") {
          copyMemo();
        }
      }
    });
  }

  // ---- SP: 項目タブ・シートの 3 段・入力中の 1 行 ----
  const tabs = qa(".js-sp-cz-tab");
  const body = q(".js-sp-cz-body");
  const setTab = (key) => {
    const k = TABS.indexOf(key) === -1 ? "logo" : key;
    panel.setAttribute("data-sp-tab", k);
    tabs.forEach((t) => {
      const on = t.getAttribute("data-sp-tab-key") === k;
      t.setAttribute("aria-selected", on ? "true" : "false");
      t.tabIndex = on ? 0 : -1;
    });
    if (body) {
      body.scrollTop = 0;
    }
  };
  const setSnap = (snap) => {
    panel.setAttribute("data-sp-snap", snap);
    panel.style.removeProperty("--sp-cz-drag-h");
    panel.classList.remove("is-dragging");
  };
  const showTabFor = (kind) => {
    setTab(kind === "image" ? "photo" : "text");
    if (panel.getAttribute("data-sp-snap") === "row") {
      setSnap("half");
    }
  };
  tabs.forEach((t) =>
    t.addEventListener("click", () => {
      setTab(t.getAttribute("data-sp-tab-key") || "logo");
      if (panel.getAttribute("data-sp-snap") === "row") {
        setSnap("half");
      }
    })
  );
  const tablist = q(".js-sp-cz-tabs");
  if (tablist) {
    tablist.addEventListener("keydown", (e) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") {
        return;
      }
      const i = tabs.findIndex((t) => t.getAttribute("aria-selected") === "true");
      const n = tabs[(i + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
      if (n) {
        n.click();
        n.focus();
      }
    });
  }
  // ハンドル: ドラッグで高さを変え、離したら 1 行 / 半分 / 85% の近い段へ。タップ（移動 6px 未満）は 1 行 → 半分 → 85% → 半分。
  const handle = q(".js-sp-cz-handle");
  const head = panel.querySelector(".p-live-customize__head");
  const snapHeights = () => {
    const vh = window.innerHeight;
    return { row: (head ? head.offsetHeight : 52) + 1, half: vh * 0.5, full: vh * 0.85 };
  };
  const nextSnap = (cur) => (cur === "row" ? "half" : cur === "half" ? "full" : "half");
  let drag = null;
  if (handle) {
    handle.addEventListener("pointerdown", (e) => {
      if (!isPhoneViewer()) {
        return;
      }
      drag = { y: e.clientY, h: panel.getBoundingClientRect().height, moved: 0, from: panel.getAttribute("data-sp-snap") || "half" };
      try {
        handle.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    });
    handle.addEventListener("pointermove", (e) => {
      if (!drag) {
        return;
      }
      const dy = e.clientY - drag.y;
      drag.moved = Math.max(drag.moved, Math.abs(dy));
      if (drag.moved < 6) {
        return;
      }
      const H = snapHeights();
      panel.classList.add("is-dragging");
      panel.setAttribute("data-sp-snap", "drag");
      // 高さはカスタムプロパティで渡し、CSS が使う（style の height は直接書かない）。
      panel.style.setProperty("--sp-cz-drag-h", `${Math.round(Math.min(window.innerHeight * 0.9, Math.max(H.row, drag.h - dy)))}px`);
    });
    const endDrag = () => {
      if (!drag) {
        return;
      }
      const d = drag;
      drag = null;
      if (d.moved < 6) {
        setSnap(nextSnap(d.from));
        return;
      }
      const h = panel.getBoundingClientRect().height;
      const best = Object.entries(snapHeights()).sort((a, b) => Math.abs(a[1] - h) - Math.abs(b[1] - h))[0][0];
      setSnap(best);
    };
    handle.addEventListener("pointerup", endDrag);
    handle.addEventListener("pointercancel", endDrag);
    handle.addEventListener("click", (e) => {
      // キーボード（Enter / Space）。ポインタの操作は pointerup で処理済み。
      if (e.detail === 0) {
        setSnap(nextSnap(panel.getAttribute("data-sp-snap") || "half"));
      }
    });
  }
  // 入力中: 入力欄（とその確定ボタン）だけの 1 行に縮め、キーボードの高さ（visualViewport から）の上に出す。
  const updateKb = () => {
    const vv = window.visualViewport;
    const kb = vv ? Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop)) : 0;
    panel.style.setProperty("--sp-kb", `${kb}px`);
  };
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", updateKb);
    window.visualViewport.addEventListener("scroll", updateKb);
  }
  const isTextInput = (t) => !!(t && t.matches && t.matches("input[type='text'], textarea") && panel.contains(t));
  let inputExitTimer = 0;
  const clearInputMarks = () => {
    panel.querySelectorAll(".is-input-unit, .is-input-path").forEach((x) => x.classList.remove("is-input-unit", "is-input-path"));
  };
  const enterInputMode = (t) => {
    clearInputMarks();
    const unit = t.closest(".p-live-customize__card") || t.closest(".p-live-customize__field");
    if (!unit) {
      return;
    }
    unit.classList.add("is-input-unit");
    for (let p = unit.parentElement; p && p !== panel; p = p.parentElement) {
      p.classList.add("is-input-path");
    }
    panel.classList.add("is-sheet-input");
    updateKb();
  };
  const exitInputMode = (toHalf = true) => {
    window.clearTimeout(inputExitTimer);
    if (!panel.classList.contains("is-sheet-input")) {
      return;
    }
    panel.classList.remove("is-sheet-input");
    clearInputMarks();
    if (toHalf) {
      setSnap("half");
    }
  };
  panel.addEventListener("focusin", (e) => {
    if (!isPhoneViewer() || !isTextInput(e.target)) {
      return;
    }
    window.clearTimeout(inputExitTimer);
    enterInputMode(e.target);
  });
  panel.addEventListener("focusout", () => {
    if (!panel.classList.contains("is-sheet-input")) {
      return;
    }
    window.clearTimeout(inputExitTimer);
    // 確定（Enter /「反映する」）でフォーカスが外れたら半分に戻す。すぐ戻すと押したボタンの位置がずれるので少し待つ。
    inputExitTimer = window.setTimeout(() => {
      if (isTextInput(doc.activeElement)) {
        return;
      }
      exitInputMode(true);
    }, 250);
  });

  // ---- 開閉・たたむ ----
  const lead = q(".js-sp-cz-lead");
  const listHelp = q(".js-sp-cz-list-help");
  const bar0 = q(".js-sp-cz-bar");
  const headActions = panel.querySelector(".p-live-customize__head-actions");
  const applyViewerMode = () => {
    const phone = isPhoneViewer();
    if (lead) {
      lead.textContent = phone ? msg("lead_sp") : msg("lead");
    }
    if (pickHint) {
      pickHint.textContent = phone ? msg("pick_hint_sp") : msg("pick_hint");
    }
    if (listHelp) {
      listHelp.textContent = phone ? msg("pick_list_help_sp") : msg("pick_list_help");
    }
    // 「変更 N 件・すべて元に戻す」: SP はシートの上端（ハンドルの横）、PC はパネル下端の操作バー。
    if (bar0 && head && headActions) {
      if (phone) {
        if (bar0.parentNode !== head) {
          head.insertBefore(bar0, head.firstChild);
        }
      } else if (bar0.parentNode !== panel) {
        panel.append(bar0);
      }
    }
    if (!panel.hasAttribute("data-sp-tab")) {
      setTab("logo");
    }
    if (!panel.hasAttribute("data-sp-snap")) {
      setSnap("half");
    }
    if (!phone) {
      exitInputMode(false);
    } else {
      collapsed = false;
    }
    if (exportShare) {
      exportShare.hidden = !(phone && typeof navigator.share === "function");
    }
    if (exportFile) {
      exportFile.hidden = phone;
    }
    renderSelection();
    render();
    sendMode();
  };
  let pushed = false;
  const render = () => {
    const phone = isPhoneViewer();
    panel.hidden = !open || (collapsed && !phone);
    if (rail) {
      rail.hidden = !(open && collapsed) || phone;
    }
    root.classList.toggle("is-customize-open", open);
    root.classList.toggle("is-customize-collapsed", open && collapsed && !phone);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (entry) {
      entry.setAttribute("aria-expanded", open ? "true" : "false");
    }
    measure();
    updateEntry();
  };
  const setOpen = (on) => {
    const wasOpen = open;
    open = on;
    if (on && !wasOpen) {
      if (isPhoneViewer()) {
        setSnap("half");
        if (!pushed && window.history && typeof window.history.pushState === "function") {
          // SP のシートだけ、開くときに履歴を 1 段積む（戻るで閉じる。PC は積まない。docs/22 §3.6）。
          pushed = true;
          try {
            window.history.pushState({ spCustomize: 1 }, "");
          } catch {
            /* noop */
          }
        }
      }
      hideLanding("");
      if (!openedOnce) {
        openedOnce = true;
        track("open");
      }
    }
    if (!on) {
      exitInputMode(false);
    }
    render();
    sendMode();
    if (on && canSend()) {
      // 最初の apply でデモが原状を記録し、ロゴの範囲などを測って manifest を返す。
      if (!manifest || state.isEmpty()) {
        sendApply();
      }
      waitManifest();
    }
    if (wasOpen && !on) {
      window.setTimeout(afterClose, 0);
    }
  };
  const setCollapsed = (on) => {
    collapsed = on && !isPhoneViewer();
    render();
    sendMode(); // 覆う幅が変わる（初回の吹き出しの位置）
  };
  const closeDock = () => {
    // SP の「その他」（preview.js のドック）が開いていれば閉じる。
    if (root.classList.contains("is-dock-open")) {
      const dock = root.querySelector(".js-sp-live-dock");
      if (dock) {
        dock.click();
      }
    }
  };
  const focusBack = () => {
    const target = visible(toggle) ? toggle : entry && !entry.hidden ? entry : null;
    if (target) {
      target.focus({ preventScroll: true });
    }
  };
  const openPanel = () => {
    collapsed = !isPhoneViewer() && !!stage && stage.clientWidth < num("collapseStageBelowPx", 1200);
    closeDock();
    setOpen(true);
  };
  toggle.addEventListener("click", () => {
    if (open) {
      collapsed = false;
      setOpen(false);
      focusBack();
      return;
    }
    openPanel();
  });
  qa(".js-sp-cz-close").forEach((b) =>
    b.addEventListener("click", () => {
      collapsed = false;
      setOpen(false);
      focusBack();
    })
  );
  const collapseBtn = q(".js-sp-cz-collapse");
  if (collapseBtn) {
    collapseBtn.addEventListener("click", () => {
      setCollapsed(true);
      const exp = q(".js-sp-cz-expand");
      if (exp) {
        exp.focus({ preventScroll: true });
      }
    });
  }
  const expandBtn = q(".js-sp-cz-expand");
  if (expandBtn) {
    expandBtn.addEventListener("click", () => setCollapsed(false));
  }
  const reloadBtn = q(".js-sp-cz-reload-top");
  if (reloadBtn) {
    reloadBtn.addEventListener("click", reloadTop);
  }
  const restoreBtn = q(".js-sp-cz-restore");
  if (restoreBtn) {
    restoreBtn.addEventListener("click", reloadTop);
  }
  window.addEventListener("popstate", () => {
    if (open && isPhoneViewer()) {
      setOpen(false);
    }
  });
  doc.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && open && panel.contains(doc.activeElement)) {
      setOpen(false);
      focusBack();
    }
  });
  if (window.matchMedia) {
    const mq = window.matchMedia("(max-width: 768px)");
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", applyViewerMode);
    }
  }
  let resizeRaf = 0;
  window.addEventListener(
    "resize",
    () => {
      window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(() => {
        measure();
        placeLanding();
      });
    },
    { passive: true }
  );
  window.addEventListener(
    "scroll",
    () => {
      window.cancelAnimationFrame(resizeRaf);
      resizeRaf = window.requestAnimationFrame(measure);
    },
    { passive: true }
  );
  // 端末切替・「その他」の開閉・全画面で上下のバーが変わる。
  if (typeof MutationObserver === "function") {
    new MutationObserver(() => {
      measure();
      placeLanding();
    }).observe(root, { attributes: true, attributeFilter: ["class"] });
  }

  // 入口を押す: 開いて、前回選んでいた場所（デモの選択枠・スクロール）と SP のタブを戻す。
  if (entry) {
    entry.addEventListener("click", () => {
      if (open) {
        return;
      }
      entry.classList.remove("is-pulse-once", "is-compact");
      collapsed = false;
      closeDock();
      setOpen(true);
      if (selected && (selected.kind === "text" || selected.kind === "image")) {
        focusRef(selected.ref);
      }
    });
  }

  // ---- 着地後の案内カード（非モーダルの吹き出し。最初の 1 回だけ。2026-10-28 人間指示: 商品を変えるたびに出さない） ----
  const landing = q(".js-sp-cz-landing");
  const readSeen = () => {
    try {
      return window.localStorage.getItem(LANDING_KEY) === "1";
    } catch {
      return false;
    }
  };
  const markSeen = () => {
    try {
      window.localStorage.setItem(LANDING_KEY, "1");
    } catch {
      /* 残せなくても動く（同じ表示中は再表示しない） */
    }
  };
  let landingDone = readSeen();
  let landingShown = false;
  let landingTimer = 0;
  const landingAnchor = () => (isPhoneViewer() ? (entry && !entry.hidden ? entry : null) : visible(toggle) ? toggle : null);
  function placeLanding() {
    if (!landingShown || !landing) {
      return;
    }
    const a = landingAnchor();
    if (!a) {
      return;
    }
    const r = a.getBoundingClientRect();
    const up = isPhoneViewer();
    landing.classList.toggle("is-up", up);
    const w = landing.offsetWidth;
    const cx = r.left + r.width / 2;
    const left = Math.round(Math.max(12, Math.min(cx - w / 2, window.innerWidth - w - 12)));
    const top = up ? Math.round(r.top - 12 - landing.offsetHeight) : Math.round(r.bottom + 12);
    // 位置はカスタムプロパティで渡し、CSS が使う。
    landing.style.setProperty("--sp-cz-landing-x", `${left}px`);
    landing.style.setProperty("--sp-cz-landing-y", `${Math.max(8, top)}px`);
    landing.style.setProperty("--sp-cz-arrow-x", `${Math.round(Math.max(18, Math.min(cx - left, w - 18)))}px`);
  }
  const showLanding = () => {
    landingTimer = 0;
    if (landingDone || landingShown || open || !landing || !everReady || !canSend() || !landingAnchor()) {
      return;
    }
    if (root.classList.contains("is-fullscreen") || root.classList.contains("is-dock-open")) {
      return;
    }
    landing.hidden = false;
    landingShown = true;
    markSeen(); // 出した時点で印を付ける（閉じずに次の商品へ移っても、もう出さない）
    placeLanding();
    void landing.offsetWidth; // 初期状態を確定させてから出す（reduced-motion は CSS で即表示）
    landing.classList.add("is-shown");
    pulse(landingAnchor(), "is-pulse");
    const title = q("#sp-cz-landing-title");
    announce(title ? title.textContent : "");
    track("landing_shown");
  };
  function hideLanding(how) {
    window.clearTimeout(landingTimer);
    landingTimer = 0;
    if (!landingDone) {
      landingDone = true;
      markSeen();
      if (how && landingShown) {
        track(how === "open" ? "landing_open" : "landing_close");
      }
    }
    [toggle, entry].forEach((b) => b && b.classList.remove("is-pulse"));
    if (!landingShown || !landing) {
      return;
    }
    landingShown = false;
    landing.classList.remove("is-shown");
    landing.hidden = true;
  }
  function scheduleLanding() {
    if (landingDone || landingShown || !landing) {
      return;
    }
    window.clearTimeout(landingTimer);
    landingTimer = window.setTimeout(showLanding, num("landingDelayMs", 1500));
  }
  if (landing) {
    landing.querySelectorAll(".js-sp-cz-landing-close").forEach((b) => b.addEventListener("click", () => hideLanding("close")));
    const tryBtn = landing.querySelector(".js-sp-cz-landing-try");
    if (tryBtn) {
      tryBtn.addEventListener("click", () => {
        hideLanding("open");
        if (!open) {
          openPanel();
        }
      });
    }
    // カードの外（親の画面）を押す / Esc / プレビューへフォーカスが移ったら閉じる。出る前の操作は待ち時間を数え直す。
    doc.addEventListener(
      "pointerdown",
      (e) => {
        if (landingShown) {
          if (!landing.contains(e.target)) {
            hideLanding("close");
          }
        } else if (landingTimer) {
          scheduleLanding();
        }
      },
      true
    );
    doc.addEventListener(
      "keydown",
      (e) => {
        if (landingShown && e.key === "Escape") {
          hideLanding("close");
        } else if (!landingShown && landingTimer) {
          scheduleLanding();
        }
      },
      true
    );
    window.addEventListener("blur", () => {
      window.setTimeout(() => {
        if (landingShown && doc.activeElement === iframe) {
          hideLanding("close");
        }
      }, 0);
    });
  }

  // ---- 受信 ----
  window.addEventListener("message", (e) => {
    if (e.source !== iframe.contentWindow) {
      return;
    }
    if (!(e.origin === "null" || e.origin === window.location.origin)) {
      return;
    }
    const d = e.data;
    if (!d || typeof d !== "object" || typeof d.type !== "string" || d.type.indexOf(NS) !== 0 || d.v !== 1) {
      return;
    }
    const name = d.type.slice(NS.length);
    if (name === "ready") {
      onReady(d);
      return;
    }
    // 親が起こしていない読み込みの後・準備前は、何が届いても欄を有効にしない。
    if (!canSend()) {
      return;
    }
    if (name === "manifest") {
      onManifest(d.manifest);
      return;
    }
    if (name === "applied") {
      onApplied(d);
      return;
    }
    if (name === "select") {
      if (!open || typeof d.ref !== "string" || !REF_RE.test(d.ref) || ["text", "image", "item", "section"].indexOf(d.kind) === -1) {
        return;
      }
      if (!manifest) {
        return;
      }
      if (collapsed) {
        setCollapsed(false);
      }
      selectRef(d.ref, d.kind);
      if (isPhoneViewer()) {
        showTabFor(d.kind);
        revealCard();
      } else {
        revealCard();
        const ta = card ? card.querySelector("textarea") : null;
        if (ta) {
          ta.focus({ preventScroll: true });
        }
      }
      return;
    }
    if (name === "scroll") {
      if (entry) {
        entry.classList.toggle("is-compact", d.moving === true && isPhoneViewer() && !entry.hidden && !landingShown);
      }
      return;
    }
    if (name === "coach") {
      if (d.done === true) {
        coachDone = true;
      }
      return;
    }
    if (name === "blocked") {
      if (d.kind === "link") {
        showToast(isPhoneViewer() ? msg("link_blocked_sp") : msg("link_blocked"));
      } else if (d.kind === "external") {
        showToast(msg("external_blocked"));
      } else if (d.kind === "form") {
        showToast(msg("form_blocked"));
      }
    }
  });

  function onApplied(d) {
    if (d.seq !== seq) {
      return;
    }
    window.clearTimeout(ackTimer);
    if (d.ok !== true) {
      trackError("error");
      showToast(msg("err_apply"), { label: msg("export_copy"), run: copyMemo });
      return;
    }
    const changed = d.changed && typeof d.changed === "object" ? d.changed : {};
    // ロゴと文字の大きさの結果は毎回受け取る（読み直し後の apply でも注意を出す）。
    lastLogoApplied =
      d.logo && typeof d.logo === "object"
        ? { applied: d.logo.applied === true, width: Math.round(Number(d.logo.width) || 0), overflow: d.logo.overflow === true }
        : null;
    lastTextSizes = Object.create(null);
    if (d.text_sizes && typeof d.text_sizes === "object") {
      for (const [ref, v] of Object.entries(d.text_sizes)) {
        if (REF_RE.test(ref) && v && typeof v === "object") {
          lastTextSizes[ref] = { overflow: v.overflow === true, more_lines: v.more_lines === true };
        }
      }
    }
    renderLogo();
    if (selected && selected.kind === "text" && card) {
      const note = card.querySelector(".js-sp-cz-size-note");
      const w = lastTextSizes[selected.ref];
      if (note) {
        note.hidden = !((state.data.text_sizes[selected.ref] || "m") !== "m" && w && (w.overflow || w.more_lines));
      }
    }
    const done = pending;
    pending = [];
    const uis = new Set();
    for (const p of done) {
      if (p.track) {
        track(p.track, p.section);
      }
      if (p.ui) {
        uis.add(p.ui);
      }
    }
    const companyOverflow = d.overflow === true || (Array.isArray(d.overflow) && d.overflow.indexOf("company") !== -1);
    if (uis.has("company")) {
      const n = Math.max(0, Math.round(Number(changed.company) || 0));
      companyStatus.textContent = state.data.company ? (n ? fmt(msg("applied_count"), n) : msg("company_not_found")) : "";
      if (companyReveal) {
        companyReveal.hidden = !n || !state.data.company;
      }
      if (companyNote) {
        companyNote.hidden = !(state.data.company && companyOverflow);
        companyNote.classList.toggle("is-warn", companyOverflow);
        if (companyOverflow) {
          companyNote.textContent = msg("company_long_note");
        }
      }
      if (n && state.data.company) {
        announce(fmt(msg("applied_count"), n));
      }
    }
    if (uis.has("image")) {
      reveal("image");
    }
    if (uis.has("logo") || uis.has("logo_width")) {
      const st = q(".js-sp-cz-logo-status");
      if (st) {
        st.textContent = state.data.logo && lastLogoApplied && lastLogoApplied.applied ? fmt(msg("logo_applied"), lastLogoApplied.width) : "";
      }
      if (uis.has("logo") && state.data.logo) {
        reveal("logo");
        if (st) {
          announce(st.textContent);
        }
      }
    }
  }

  // ---- 起動 ----
  measure();
  if (typeof ResizeObserver === "function") {
    const ro = new ResizeObserver(() => measure());
    [bar, foot, mini].forEach((n) => n && ro.observe(n));
  }
  setTab("logo");
  applyViewerMode();
  refreshAll();
}
